import { useState, useEffect, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/base/Button';

import {
  RiYoutubeFill,
  RiTwitterXFill,
  RiRedditFill,
  RiInstagramFill,
} from 'react-icons/ri';

interface GlobalSettings {
  youtube_url?: string;
  twitter_url?: string;
  reddit_url?: string;
  instagram_url?: string;
  footer_description?: string;
}

export default function FooterWithNewsletter() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('');
  const [globals, setGlobals] = useState<GlobalSettings>({});

  // Fetch global settings from WP REST API
  useEffect(() => {
    fetch('https://blog.thecryptotutor.com/wp-json/custom/v1/globals')
      .then((res) => res.json())
      .then((data) => setGlobals(data))
      .catch((err) => console.error('Failed to fetch globals:', err));
  }, []);

  const MAILCHIMP_U = 'e497c4d40a423ce7cd5d95221';
  const MAILCHIMP_ID = '1e184fb803';
  const MAILCHIMP_FID = '0034c2e1f0';
  const MAILCHIMP_DC = 'us1';
  const MAILCHIMP_URL = `https://${MAILCHIMP_DC}.list-manage.com/subscribe/post-json?u=${MAILCHIMP_U}&id=${MAILCHIMP_ID}&f_id=${MAILCHIMP_FID}&c=?`;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    setStatus('Submitting...');

    try {
      const url = `${MAILCHIMP_URL}&EMAIL=${encodeURIComponent(email)}`;
      await new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        const callbackName = `jsonpCallback_${Date.now()}`;

        (window as any)[callbackName] = (data: any) => {
          if (data.result === 'success') {
            setStatus('Subscribed successfully! 🎉');
            setEmail('');
          } else {
            setStatus(data.msg || 'Subscription failed. 😕');
          }
          resolve();
          delete (window as any)[callbackName];
          script.remove();
        };

        script.src = url.replace('=?', `=${callbackName}`);
        script.onerror = () => {
          setStatus('Something went wrong. Please try again.');
          reject();
        };
        document.body.appendChild(script);
      });
    } catch (err) {
      console.error(err);
      setStatus('Something went wrong. Please try again.');
    }

    setTimeout(() => setStatus(''), 5000);
  };

  return (
    <>
      {/* Newsletter Section */}
      <section className="bg-[#F5F7FA] py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200 flex flex-col md:flex-row items-center md:justify-between space-y-6 md:space-y-0">
            <div className="max-w-md text-center md:text-left">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                Subscribe to Our Newsletter
              </h2>
              <p className="text-gray-600">
                Get the latest crypto news, insights, and tips directly in your inbox.
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              className="flex flex-col sm:flex-row gap-4 w-full max-w-lg"
            >
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
              />

              <Button
                type="submit"
                className="bg-[#FF5671] hover:bg-red-600 text-white px-6 py-3 rounded-lg"
              >
                Subscribe
              </Button>
            </form>
          </div>

          <div className="mt-4 h-1 w-full flex rounded-b-lg overflow-hidden">
            <div className="flex-1 bg-[#FFDC67]"></div>
            <div className="flex-1 bg-[#897BFF]"></div>
            <div className="flex-1 bg-[#FFDC67]"></div>
            <div className="flex-1 bg-[#71CFB1]"></div>
          </div>

          {status && <p className="mt-4 text-center text-gray-700">{status}</p>}
        </div>
      </section>

      {/* Footer Section */}
      <footer className="bg-white border border-gray-200 rounded-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Logo + Description */}
            <div className="col-span-1 md:col-span-1">
              <div className="flex flex-col items-center mb-4 w-fit">
                <img
                  src="http://blog.thecryptotutor.com/wp-content/uploads/2025/11/ChatGPT-Image-15.-Mai-2025_-17_51_48.png"
                  alt="The Crypto Tutor Logo"
                  className="w-16 h-16 rounded-lg object-cover mb-2"
                />
                <span className="text-lg font-bold text-gray-900 text-center">
                  The Crypto Tutor
                </span>
              </div>

              <p className="text-gray-600 text-sm leading-relaxed">
                {globals.footer_description ||
                  'Your trusted guide for everything crypto. Learn, trade, and grow with expert insights and comprehensive education.'}
              </p>
            </div>

            {/* Categories */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">
                <a href="#categories" className="hover:text-blue-600">Categories</a>
              </h4>

              <ul className="space-y-2 text-sm">
                <li><Link to="/category/analysis" className="text-gray-600 hover:text-blue-600">Analysis</Link></li>
                <li><Link to="/category/defi" className="text-gray-600 hover:text-blue-600">DeFi</Link></li>
                <li><Link to="/category/guide" className="text-gray-600 hover:text-blue-600">Guide</Link></li>
                <li><Link to="/category/news" className="text-gray-600 hover:text-blue-600">News</Link></li>
                <li><Link to="/category/reviews" className="text-gray-600 hover:text-blue-600">Reviews</Link></li>
                <li><Link to="/category/trading" className="text-gray-600 hover:text-blue-600">Trading</Link></li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">
                <a href="#support" className="hover:text-blue-600">Support</a>
              </h4>

              <ul className="space-y-2 text-sm">
                <li><Link to="/contact" className="text-gray-600 hover:text-blue-600">Help</Link></li>
                <li><Link to="/contact" className="text-gray-600 hover:text-blue-600">Contact Us</Link></li>
                <li><Link to="/privacy-policy" className="text-gray-600 hover:text-blue-600">Privacy Policy</Link></li>
                <li><Link to="/terms-of-use" className="text-gray-600 hover:text-blue-600">Terms</Link></li>
              </ul>
            </div>

            {/* Social Icons — UPDATED */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Social</h4>

              <div className="flex space-x-3">
                {globals.youtube_url && (
                  <a href={globals.youtube_url} className="text-gray-400 hover:text-blue-600">
                    <RiYoutubeFill className="text-xl" />
                  </a>
                )}

                {globals.twitter_url && (
                  <a href={globals.twitter_url} className="text-gray-400 hover:text-blue-600">
                    <RiTwitterXFill className="text-xl" />
                  </a>
                )}

                {globals.reddit_url && (
                  <a href={globals.reddit_url} className="text-gray-400 hover:text-blue-600">
                    <RiRedditFill className="text-xl" />
                  </a>
                )}

                {globals.instagram_url && (
                  <a href={globals.instagram_url} className="text-gray-400 hover:text-blue-600">
                    <RiInstagramFill className="text-xl" />
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="mt-8 pt-8 border-t border-gray-200 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-500 text-sm">
              The Crypto Tutor - 2025. All Rights Reserved.
            </p>
          </div>
        </div>
      </footer>
    </>
  );
}
